import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Typography, Input, Form } from 'antd';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import { CbDeliverableItemV2Status } from 'api/graphql-global-types';
import { CampaignTaskType } from 'api/graphql-global-types';
// Constants
import { POST_DELIVERABLE_LINK_TYPES } from 'constants/deliverables';
// Helpers
import { deliverableHasContentFileItems } from 'helpers/deliverables';
// Components
import DropdownItem from '../DropdownItem/DropdownItem';
// Styles
import styles from './SubmitCopy.module.scss';

const { Text } = Typography;
const { TextArea } = Input;

type SubmitCopyProps = {
  item?: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2 | null;
  deliverable?: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2 | null;
};

const SubmitCopy: React.FC<SubmitCopyProps> = ({ item, deliverable }) => {
  const methods = useFormContext();
  const copyEntered = methods.watch('postCopy.copy');
  const isRejected = item?.status === CbDeliverableItemV2Status.REJECTED;
  const isApproved = item?.status === CbDeliverableItemV2Status.APPROVED;

  // Check if this is a link type task, but only if the deliverable doesn't have content file items
  const isLinkType =
    item?.task &&
    'type' in item.task &&
    POST_DELIVERABLE_LINK_TYPES.includes(item.task.type as CampaignTaskType) &&
    !(deliverable && deliverableHasContentFileItems(deliverable));

  useEffect(() => {
    if (isApproved) {
      methods.setValue('postCopy.copy', item?.copy);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  return (
    <div className={styles.root}>
      <div className={styles.titleContainer}>
        <div className={styles.title}>
          <Text>
            {isLinkType ? 'Social Post Link' : 'Caption'} - {item?.task?.title}
          </Text>
          {isRejected && (
            <span className={styles.rejected}>Changes Needed</span>
          )}
        </div>
        {isApproved ? (
          <span className={styles.approved}>Approved</span>
        ) : (
          <span className={copyEntered ? styles.approved : styles.disabled}>
            {isLinkType ? 'Link Entered' : 'Copy Entered'}
          </span>
        )}
      </div>

      {!isApproved && (
        <>
          <DropdownItem
            label="Description"
            labelClassName={styles.dropdownLabel}
            className={styles.dropdownContainer}
            defaultExpanded
          >
            {item?.task?.description}
          </DropdownItem>
          {!isLinkType && (
            <DropdownItem
              label="Suggested Caption"
              labelClassName={styles.dropdownLabel}
              className={styles.dropdownContainer}
              defaultExpanded
            >
              {item?.task && 'suggestedCaption' in item.task
                ? (item.task as any).suggestedCaption
                : null}
            </DropdownItem>
          )}
        </>
      )}
      <input
        type="hidden"
        name={`postCopy.deliverableItemId`}
        ref={methods.register}
        value={item?.id}
      />
      <Controller
        control={methods.control}
        label={isLinkType ? 'Social Post Link' : 'Post Caption'}
        name="postCopy.copy"
        render={({ onChange, ...rest }) => {
          return isLinkType ? (
            <Input
              placeholder="Paste your social media post link here"
              disabled={isApproved}
              onChange={onChange}
              {...rest}
            />
          ) : (
            <Form.Item
              validateStatus={methods.errors?.postCopy?.copy ? 'error' : ''}
              help={methods.errors?.postCopy?.copy?.message}
            >
              <TextArea
                rows={4}
                placeholder="Please include what you will use for any captions that are required for posts to be reviewed and approved by brand"
                disabled={isApproved}
                onChange={onChange}
                {...rest}
                onErrorCapture={methods.errors?.postCopy?.copy?.message}
              />
            </Form.Item>
          );
        }}
      />
    </div>
  );
};

export default SubmitCopy;
